import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import {
  ActionFailed,
  ConfirmDialogService,
  Destroyable,
  DialogService,
  isSomething,
  SaveForm,
  selectSome,
  Toaster,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { select, Store } from '@ngrx/store'
import { OnInit, Component, Inject } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'

import { Actions, ofType } from '@ngrx/effects'
import { AddCharge, AddChargeComplete, ChargeResponse, CreateChargePayload } from '@tokeet-frontend/charges'
import { AddBillingDialogComponent } from '@tv3/shared/add-billing/add-billing-dialog.component'
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators'
import { AddInquiryCardComplete } from '@tv3/store/inquiry/inquiry-fields.actions'
import { selectInquiriesByIds } from '@tv3/store/inquiry/inquiry.selectors'
import { FetchInquiries } from '@tv3/store/inquiry/inquiry.actions'

export function getChargePayloadFromForm(form: FormGroup) {
  const formData = form.getRawValue()
  return {
    rental_id: formData.rental_id,
    guest_id: formData.guest_id,
    inquiry_id: formData.inquiry_id,
    extra_id: formData.extra_id,
    amount: formData.amount,
    description: formData.description,
  }
}

@Component({
  selector: 'app-charge-dialog',
  templateUrl: './charge-dialog.component.html',
  styleUrls: ['./charge-dialog.component.scss'],
  host: { class: 'modal-content' },
})
export class ChargeDialogComponent extends Destroyable implements OnInit {
  isDuplicate = false
  isEdit = false
  isLoading = false

  get charge() {
    return this.data.charge
  }

  form = this.fb.group({
    rental_id: ['', [Validators.required]],
    guest_id: ['', [Validators.required]],
    inquiry_id: ['', [Validators.required]],
    extra_id: [],
    amount: ['', [Validators.required]],
    description: ['', [Validators.minLength(2), Validators.maxLength(500)]],
  })

  booking: Inquiry

  constructor(
    private fb: FormBuilder,
    private store: Store<any>,
    private actions: Actions,
    private toaster: Toaster,
    private dialog: DialogService,
    private confirm: ConfirmDialogService,
    public dialogRef: MatDialogRef<ChargeDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      charge?: ChargeResponse
      duplicate?: boolean
      defaults?: Partial<CreateChargePayload>
    }
  ) {
    super()
    this.isDuplicate = this.data.duplicate
    this.isEdit = !this.isDuplicate && !!this.charge?.pkey
  }

  ngOnInit(): void {
    this.form
      .get('inquiry_id')
      .valueChanges.pipe(
        filter(isSomething),
        distinctUntilChanged(),
        tap((id) => this.store.dispatch(FetchInquiries({ ids: [id] }))),
        switchMap((id) =>
          this.store.pipe(
            selectSome(selectInquiriesByIds([id])),
            map(([b]) => b)
          )
        ),
        untilDestroy(this)
      )
      .subscribe((b) => {
        this.booking = b
        this.form.patchValue({ rental_id: b.rentalId, guest_id: b.guestId })
      })

    this.actions.pipe(ofType(AddChargeComplete), untilDestroy(this)).subscribe(() => {
      this.close()
    })
    this.actions.pipe(ofType(ActionFailed), untilDestroy(this)).subscribe(() => {
      this.isLoading = false
    })
    this.actions.pipe(ofType(AddInquiryCardComplete), untilDestroy(this)).subscribe(() => {
      this.toaster.success($localize`You can charge with the saved card now`)
    })

    if (this.charge || this.data.defaults) {
      this.form.patchValue(this.charge || this.data.defaults)
    }
    const isFromExtra = !!this.data?.defaults?.extra_id
    if (this.isEdit) {
      this.form.disable()
    } else if (isFromExtra) {
      this.form.get('inquiry_id').disable()
      this.form.get('amount').disable()
    }
  }

  close() {
    this.dialogRef.close()
  }

  create() {
    this.isLoading = true
    this.store.dispatch(AddCharge({ payload: getChargePayloadFromForm(this.form) }))
  }

  @SaveForm()
  onSave(form: FormGroup) {
    if (isSomething(this.booking?.billing)) {
      this.create()
    } else {
      this.confirm
        .confirm({
          title: $localize`Billing Details is Missing`,
          body: $localize`Billing Details is not added on this booking, please add it first before you can add charge`,
          confirmText: $localize`Add Billing Details`,
        })
        .pipe(
          switchMap(() =>
            this.dialog
              .openFixedDialog(AddBillingDialogComponent, { width: '700px', data: { inquiry: this.booking } })
              .afterClosed()
          )
        )
        .subscribe()
    }
  }
}
